import {
  dayjs,
  formatWeekdayMonthAndYear,
  fullDurationStringWithTimes,
  startDayShortString,
  startEndTime,
} from "@outschool/time";
import _ from "lodash";

import * as Activity from "./Activity";
import * as Enrollment from "./Enrollment";

export function oneLineTimeString(meeting: $TSFixMe, timeZone: $TSFixMe) {
  return fullDurationStringWithTimes(
    meeting.start_time,
    meeting.end_time,
    timeZone,
  );
}

export function twoLineDateAndTimeStrings(
  meeting: $TSFixMe,
  timeZone: $TSFixMe,
) {
  return {
    date: formatWeekdayMonthAndYear(meeting.start_time, timeZone),
    time: startEndTime(
      dayjs(meeting.start_time),
      dayjs(meeting.end_time),
      timeZone,
      false,
    ),
  };
}

export function nextMeeting(meetings: $TSFixMe) {
  return meetings.filter((m: $TSFixMe) => dayjs(m.start_time).isAfter())[0];
}

export function currentOrNextMeeting(sortedMeetings: $TSFixMe) {
  return sortedMeetings.filter((m: $TSFixMe) => dayjs(m.end_time).isAfter())[0];
}

export function isPast(meeting: $TSFixMe) {
  return meeting && dayjs(meeting.end_time).isBefore();
}

export function isFuture(meeting: $TSFixMe) {
  return meeting && meeting.start_time && dayjs(meeting.start_time).isAfter();
}

export function isInProgress(meeting: $TSFixMe) {
  return (
    meeting &&
    dayjs(meeting.end_time).isAfter() &&
    dayjs(meeting.start_time).isBefore()
  );
}

/**
 * Returns a schedule summary for given meetings.
 * Remark: It assumes that meetings are sorted by start time
 */
export function scheduleSummary(meetings: $TSFixMe, timeZone: $TSFixMe) {
  const summary = (
    scheduleSummaryStrategies.find(({ test }) => test(meetings, timeZone)) || {}
  ).summary;
  if (summary) {
    return summary(meetings, timeZone);
  }
  return null;
}

const scheduleSummaryStrategies = [
  {
    // one time
    // eg: Mon Mar 6, 10am - 11am Pacific
    test(meetings: $TSFixMe) {
      return meetings.length === 1;
    },
    summary(meetings: $TSFixMe, timeZone: $TSFixMe) {
      return fullDurationStringWithTimes(
        meetings[0].start_time,
        meetings[0].end_time,
        timeZone,
      );
    },
  },
  {
    // two days
    // eg: Meets on Monday and Tuesday
    test(meetings: $TSFixMe, timeZone: $TSFixMe) {
      if (meetings.length !== 2) {
        return false;
      }
      const firstDay = meetings[0].start_time;
      const firstDayEnd = meetings[0].end_time;
      return meetings.every(
        (m: $TSFixMe, i: $TSFixMe) =>
          dayjs.tz(firstDay, timeZone).add(i, "day").isSame(m.start_time) &&
          dayjs.tz(firstDayEnd, timeZone).add(i, "day").isSame(m.end_time),
      );
    },
    summary(meetings: $TSFixMe, timeZone: $TSFixMe) {
      const start = dayjs.tz(meetings[0].start_time, timeZone).format("dddd");
      const end = dayjs
        .tz(meetings[meetings.length - 1].start_time, timeZone)
        .format("dddd");
      return `Meets on ${start} and ${end}`;
    },
  },
  {
    // weekly
    // eg: Meets 4 times, on Mondays
    test(meetings: $TSFixMe, timeZone: $TSFixMe) {
      const firstDay = meetings[0].start_time;
      const firstDayEnd = meetings[0].end_time;
      return meetings.every(
        (m: $TSFixMe, i: $TSFixMe) =>
          dayjs.tz(firstDay, timeZone).add(i, "week").isSame(m.start_time) &&
          dayjs.tz(firstDayEnd, timeZone).add(i, "week").isSame(m.end_time),
      );
    },
    summary(meetings: $TSFixMe, timeZone: $TSFixMe) {
      const count = meetings.length;
      const day = dayjs.tz(meetings[0].start_time, timeZone).format("dddd");
      return `Meets ${count} times, on ${day}s`;
    },
  },
  {
    // bi-weekly
    // eg: Meets 4 times, every other Wednesday
    test(meetings: $TSFixMe, timeZone: $TSFixMe) {
      const firstDay = meetings[0].start_time;
      const firstDayEnd = meetings[0].end_time;
      return meetings.every(
        (m: $TSFixMe, i: $TSFixMe) =>
          dayjs
            .tz(firstDay, timeZone)
            .add(i * 2, "week")
            .isSame(m.start_time) &&
          dayjs
            .tz(firstDayEnd, timeZone)
            .add(i * 2, "week")
            .isSame(m.end_time),
      );
    },
    summary(meetings: $TSFixMe, timeZone: $TSFixMe) {
      const count = meetings.length;
      const day = dayjs.tz(meetings[0].start_time, timeZone).format("dddd");
      return `Meets ${count} times, every other ${day}`;
    },
  },
  {
    // consecutive days (one time)
    // eg: Meets Tuesday through Thursday
    test(meetings: $TSFixMe, timeZone: $TSFixMe) {
      const firstDay = meetings[0].start_time;
      const firstDayEnd = meetings[0].end_time;
      return meetings.every(
        (m: $TSFixMe, i: $TSFixMe) =>
          dayjs.tz(firstDay, timeZone).add(i, "day").isSame(m.start_time) &&
          dayjs.tz(firstDayEnd, timeZone).add(i, "day").isSame(m.end_time),
      );
    },
    summary(meetings: $TSFixMe, timeZone: $TSFixMe) {
      const start = dayjs.tz(meetings[0].start_time, timeZone).format("dddd");
      const end = dayjs
        .tz(meetings[meetings.length - 1].start_time, timeZone)
        .format("dddd");
      return `Meets ${start} through ${end}`;
    },
  },
  {
    // two days a week one day in between
    // eg: Meets 4 times every Monday and Wednesday
    test(meetings: $TSFixMe, timeZone: $TSFixMe) {
      if (meetings.length % 2 !== 0) {
        return false;
      }
      for (let i = 0; i < meetings.length; i += 2) {
        const firstDay = meetings[i].start_time;
        const firstDayEnd = meetings[i].end_time;
        const secondDay = meetings[i + 1].start_time;
        const secondDayEnd = meetings[i + 1].end_time;
        if (
          !dayjs.tz(firstDay, timeZone).add(2, "day").isSame(secondDay) ||
          !dayjs.tz(firstDayEnd, timeZone).add(2, "day").isSame(secondDayEnd)
        ) {
          return false;
        }
      }
      return true;
    },
    summary(meetings: $TSFixMe, timeZone: $TSFixMe) {
      const count = meetings.length;
      const firstDay = dayjs
        .tz(meetings[0].start_time, timeZone)
        .format("dddd");
      const secondDay = dayjs
        .tz(meetings[1].start_time, timeZone)
        .format("dddd");
      return `Meets ${count} times, every ${firstDay} and ${secondDay}`;
    },
  },
  {
    // weekly, one week off
    // eg: Meets 4 times, on Mondays, except on Mar 20th
    test(meetings: $TSFixMe, timeZone: $TSFixMe) {
      if (meetings.length < 3) {
        return false;
      }
      const firstDay = meetings[0].start_time;
      const firstDayEnd = meetings[0].end_time;
      let match = 0;
      let shift = 0;
      for (let i = 0; i < meetings.length; i++) {
        if (
          dayjs
            .tz(firstDay, timeZone)
            .add(i + shift, "week")
            .isSame(meetings[i].start_time) &&
          dayjs
            .tz(firstDayEnd, timeZone)
            .add(i + shift, "week")
            .isSame(meetings[i].end_time)
        ) {
          match++;
        } else {
          shift++;
        }
      }
      return shift === 1 && match + shift === meetings.length;
    },
    summary(meetings: { start_time: dayjs.ConfigType }[], timeZone: string) {
      const count = meetings.length;
      const day = dayjs.tz(meetings[0].start_time, timeZone).format("dddd");
      const firstDay = meetings[0].start_time;
      let exceptionDay: dayjs.Dayjs | null = null;
      for (let i = 0; i < meetings.length; i++) {
        if (
          !dayjs
            .tz(firstDay, timeZone)
            .add(i, "week")
            .isSame(meetings[i].start_time)
        ) {
          exceptionDay = dayjs
            .tz(meetings[i].start_time, timeZone)
            .subtract(1, "week");
          break;
        }
      }
      return `Meets ${count} times, on ${day}s, except on ${startDayShortString(
        exceptionDay,
        timeZone,
      )}`;
    },
  },
  {
    // fallback
    // eg: Meets x times
    test() {
      return true;
    },
    summary(meetings: $TSFixMe) {
      const count = meetings.length;
      return `Meets ${count} times`;
    },
  },
];

interface GroupIntoMeetingListingsParams {
  userTimeZone: string;
  enrollments: any[];
  cutoff?: dayjs.Dayjs | null;
  flexClassMeetingGranularity?: dayjs.OpUnitType & dayjs.ManipulateType;
}

export function groupIntoMeetingListings({
  userTimeZone,
  enrollments,
  cutoff = null,
  flexClassMeetingGranularity = "month",
}: GroupIntoMeetingListingsParams) {
  const scheduleStart = cutoff || dayjs.tz(userTimeZone).startOf("day");
  const scheduleEnrollments = enrollments.filter(e => {
    const { section } = e;
    const { activity } = section;

    return (
      Enrollment.isValid(e) &&
      !e.section?.activity?.isClub &&
      (scheduleStart.isBefore(dayjs(e.section.end_time)) ||
        activity.hasTeacherSchedule)
    );
  });

  const meetingListings: any = {};
  scheduleEnrollments.forEach(enrollment => {
    const { section } = enrollment;
    const { activity } = section;
    let meetings = section.meetings;
    if (Activity.isFlexSchedule(activity)) {
      // Create one fake "meeting" per month
      meetings = [];
      const sectionEnd = dayjs.tz(section.end_time, userTimeZone);
      let meetingStart = dayjs.tz(section.start_time, userTimeZone);
      while (meetingStart.isBefore(sectionEnd)) {
        let meetingEnd = dayjs(meetingStart).endOf(flexClassMeetingGranularity);
        if (meetingEnd.isAfter(sectionEnd)) {
          meetingEnd = sectionEnd;
        }
        meetings.push({
          start_time: meetingStart.toDate(),
          end_time: meetingEnd.toDate(),
          key: section.uid + "" + meetings.length,
        });

        meetingStart = dayjs
          .tz(meetingEnd, userTimeZone)
          .add(1, flexClassMeetingGranularity)
          .startOf(flexClassMeetingGranularity);
      }
    }

    if (!!activity.is_ongoing_weekly) {
      meetings = meetings.filter((meeting: $TSFixMe) =>
        isInRange(
          meeting,
          enrollment.ongoingStartTime ?? enrollment.ongoing_start_time,
          Boolean(enrollment.pauseWindow)
            ? undefined
            : enrollment.ongoingEndTime ?? enrollment.ongoing_end_time,
        ),
      );
    }

    meetings
      .filter((meeting: $TSFixMe) =>
        scheduleStart.isBefore(dayjs(meeting.end_time)),
      )
      .forEach((meeting: $TSFixMe) => {
        const meetingKey = meeting.uid || meeting.key; // fake flex meetings
        meetingListings[meetingKey] = meetingListings[meetingKey] || {
          // meetingListing object:
          meeting,
          section,
          activity,
          enrollments: [],
        };
        meetingListings[meetingKey].enrollments.push(enrollment);
      });
  });

  return _.sortBy(meetingListings, m => m.meeting.start_time);
}

export function isInRange(
  meeting: $TSFixMe,
  startTime: dayjs.ConfigType,
  endTime: dayjs.ConfigType = null,
) {
  return (
    !dayjs(startTime).isAfter(meeting.start_time) &&
    (!endTime || !dayjs(endTime).isBefore(meeting.end_time))
  );
}
